// Thin Connect-BFF client used from inside mail-web. The Gmail-style chat
// dock calls into this — it intentionally only exposes the endpoints the
// dock needs (directory search + DM list / start / send), not the full
// Connect API surface.

const baseUrl = import.meta.env.VITE_CONNECT_BFF_BASE_URL;

if (!baseUrl) {
    throw new Error(
        "VITE_CONNECT_BFF_BASE_URL is not set — the chat dock needs a Connect BFF origin."
    );
}

export interface DirectoryUser {
    userId: string;
    email: string;
    displayName: string | null;
    tenantId: string;
}

export interface ChatDm {
    id: string;
    participantA: string;
    participantB: string;
}

/** One row of Meeting's unified chat list (1:1 and group chats; meeting chats are left out in Mail). */
export interface ChatListItem {
    kind: "dm" | "group" | "meeting";
    id: string;
    title: string;
    peerUserId: string | null;
    peerEmail: string | null;
    memberCount: number;
    lastMessage: { authorName: string | null; snippet: string; sentAt: string; mine: boolean; system?: boolean } | null;
    unreadCount: number;
}

/** The chat a window shows: a 1:1 or a group chat. */
export interface ChatTarget {
    kind: "dm" | "group";
    id: string;
    title?: string;
    peerUserId?: string | null;
}

export interface ChatMessage {
    id: string;
    containerId: string;
    parentMessageId: string | null;
    authorUserId: string;
    authorEmail: string;
    authorDisplayName: string | null;
    bodyHtml: string;
    isEdited: boolean;
    isPinned: boolean;
    sentAt: string;
    editedAt: string | null;
}

async function fetchJson<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers });
    if (!res.ok) throw new Error(`Connect BFF ${path} → ${res.status}`);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

export interface PresenceEntry {
    userId: string;
    status: string;
}

export const chatConnectClient = {
    searchDirectory: (q: string, token: string | undefined) =>
        fetchJson<DirectoryUser[]>(
            `/api/directory/users?q=${encodeURIComponent(q)}&take=10`,
            token
        ),

    /**
     * Lists users in the caller's tenant — powers the Gmail-style roster
     * in mail-web's left rail. No query string needed, the caller's
     * tenant id comes from their access token.
     */
    listTenantUsers: (token: string | undefined, take = 100) =>
        fetchJson<DirectoryUser[]>(`/api/directory/tenant?take=${take}`, token),

    /** Online-now snapshot keyed by user id. */
    listPresence: (token: string | undefined) =>
        fetchJson<PresenceEntry[]>("/api/presence", token),

    /**
     * Resolves a single user by id via the by-id endpoint. Identity stores
     * user ids as GUIDs (serialised without dashes), so the endpoint
     * accepts either representation. Returns null on 404 — e.g. if the
     * caller's cross-tenant policy blocks them from seeing this user.
     */
    lookupUser: async (userId: string, token: string | undefined): Promise<DirectoryUser | null> => {
        if (!userId) return null;
        try {
            return await fetchJson<DirectoryUser>(
                `/api/directory/users/${encodeURIComponent(userId)}`,
                token
            );
        } catch {
            return null;
        }
    },

    listDms: (token: string | undefined) => fetchJson<ChatDm[]>("/api/dms", token),

    listChats: (token: string | undefined) => fetchJson<ChatListItem[]>("/api/chats", token),

    listMessages: (kind: "dm" | "group", id: string, token: string | undefined) =>
        fetchJson<ChatMessage[]>(kind === "dm" ? `/api/dms/${id}/messages` : `/api/conversations/${id}/messages`, token),

    sendMessage: (kind: "dm" | "group", id: string, bodyHtml: string, token: string | undefined) =>
        fetchJson<ChatMessage>(kind === "dm" ? `/api/dms/${id}/messages` : `/api/conversations/${id}/messages`, token, {
            method: "POST",
            body: JSON.stringify({ bodyHtml })
        }),

    markRead: (kind: "dm" | "group", id: string, token: string | undefined) =>
        fetchJson<{ at: string }>(kind === "dm" ? `/api/dms/${id}/read` : `/api/conversations/${id}/read`, token, { method: "POST" }),

    startDm: (otherUserId: string, token: string | undefined) =>
        fetchJson<ChatDm>("/api/dms", token, {
            method: "POST",
            body: JSON.stringify({ otherUserId })
        }),

    listDmMessages: (chatId: string, token: string | undefined) =>
        fetchJson<ChatMessage[]>(`/api/dms/${chatId}/messages`, token),

    sendDmMessage: (chatId: string, bodyHtml: string, token: string | undefined) =>
        fetchJson<ChatMessage>(`/api/dms/${chatId}/messages`, token, {
            method: "POST",
            body: JSON.stringify({ bodyHtml })
        })
};

export const connectBaseUrl = baseUrl;

/**
 * Normalises any user-id representation to the canonical no-dash lower-case
 * hex form used by the Connect BFF for DM participants and SignalR user
 * groups. The OIDC `sub` claim arrives dashed; the directory returns
 * no-dash; the DB now stores no-dash — comparing without normalising
 * means "me" never matches a participant row.
 */
export function normalizeUserId(userId: string | undefined | null): string {
    if (!userId) return "";
    // Match `Guid.TryParse` on the server — accept dashed, braced, or plain.
    const hex = userId.replace(/[{}\-]/g, "").toLowerCase();
    return /^[0-9a-f]{32}$/.test(hex) ? hex : userId.toLowerCase();
}
